import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import useEmblaCarousel from "embla-carousel-react";
import Autoplay from "embla-carousel-autoplay";
import { ChevronLeft, ChevronRight, X, Play, Pause, Twitter, Facebook, Linkedin, Mail, MessageSquare, Link2, Check, Share2 } from "lucide-react";

const SITE_ORIGIN = "https://commonsensedemocrats.us";

// Build the canonical share URL for a single photo/video item.
function photoShareUrl(item) {
  const base = (item.src || "").replace(/\.[^.]+$/, ""); // e.g. "community-005"
  return `${SITE_ORIGIN}/#photo-${base}`;
}

function gallerySectionUrl() {
  return `${SITE_ORIGIN}/#recap`;
}

/**
 * RecapCarousel
 * Loads /recap/manifest.json (produced by the image/video processing script)
 * and renders a filterable hero carousel with a full-screen lightbox.
 *
 * Manifest shape:
 * {
 *   community:  [{ src: "community-001.jpg", w, h, type: "image" }, ...],
 *   leadership: [{ src: "leadership-001.jpg", w, h, type: "image" }, ...]
 * }
 * Videos in the arrays use `type: "video"` and include a `poster` path.
 */
export default function RecapCarousel() {
  const [manifest, setManifest] = useState({ community: [], leadership: [] });
  const [filter, setFilter] = useState("all");
  const [lightboxIndex, setLightboxIndex] = useState(null);
  const [isPlaying, setIsPlaying] = useState(true);
  const [copiedKey, setCopiedKey] = useState(null); // "gallery" | "photo" | null
  const rootRef = useRef(null);

  // Load manifest on mount
  useEffect(() => {
    let cancelled = false;
    fetch("/recap/manifest.json", { cache: "no-store" })
      .then((r) => (r.ok ? r.json() : { community: [], leadership: [] }))
      .then((data) => {
        if (!cancelled) setManifest(data || { community: [], leadership: [] });
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, []);

  // Compute the active list of media based on the filter chip.
  const items = useMemo(() => {
    const withPath = (arr, event) =>
      (arr || []).map((m) => ({
        ...m,
        event,
        url: `/recap/${event}/${m.src}`,
        thumb: `/recap/${event}/thumbs/${m.src}`,
        posterUrl: m.poster ? `/recap/${event}/${m.poster}` : null,
      }));
    const all = [
      ...withPath(manifest.community, "community"),
      ...withPath(manifest.leadership, "leadership"),
    ];
    if (filter === "community") return all.filter((i) => i.event === "community");
    if (filter === "leadership") return all.filter((i) => i.event === "leadership");
    return all;
  }, [manifest, filter]);

  const counts = {
    all: (manifest.community?.length || 0) + (manifest.leadership?.length || 0),
    community: manifest.community?.length || 0,
    leadership: manifest.leadership?.length || 0,
  };

  // Autoplay plugin ref so we can pause/resume based on user actions.
  const autoplay = useRef(
    Autoplay({ delay: 4000, stopOnInteraction: false, stopOnMouseEnter: true })
  );

  const [emblaRef, emblaApi] = useEmblaCarousel(
    { loop: true, align: "center" },
    [autoplay.current]
  );

  const [selectedIndex, setSelectedIndex] = useState(0);
  const [scrollSnaps, setScrollSnaps] = useState([]);

  const onSelect = useCallback((api) => {
    if (!api) return;
    setSelectedIndex(api.selectedScrollSnap());
  }, []);

  useEffect(() => {
    if (!emblaApi) return;
    setScrollSnaps(emblaApi.scrollSnapList());
    emblaApi.on("select", onSelect);
    emblaApi.on("reInit", () => setScrollSnaps(emblaApi.scrollSnapList()));
    onSelect(emblaApi);
  }, [emblaApi, onSelect]);

  // Reset the carousel to the first slide whenever the active filter changes
  // so we do not land on an out-of-range index.
  useEffect(() => {
    if (!emblaApi) return;
    emblaApi.reInit();
    emblaApi.scrollTo(0, true);
  }, [filter, emblaApi]);

  const scrollPrev = useCallback(() => emblaApi && emblaApi.scrollPrev(), [emblaApi]);
  const scrollNext = useCallback(() => emblaApi && emblaApi.scrollNext(), [emblaApi]);

  const toggleAutoplay = useCallback(() => {
    const ap = autoplay.current;
    if (!ap) return;
    if (isPlaying) {
      ap.stop();
      setIsPlaying(false);
    } else {
      ap.play();
      setIsPlaying(true);
    }
  }, [isPlaying]);

  // Copy a URL to the clipboard, briefly flashing the "Copied" confirmation.
  const copyToClipboard = useCallback(async (url, key) => {
    try {
      await navigator.clipboard.writeText(url);
    } catch {
      // fall back: create a temporary textarea (older Safari / non-secure origins)
      const t = document.createElement("textarea");
      t.value = url;
      t.style.position = "fixed";
      t.style.opacity = "0";
      document.body.appendChild(t);
      t.select();
      try { document.execCommand("copy"); } catch {}
      document.body.removeChild(t);
    }
    setCopiedKey(key);
    setTimeout(() => setCopiedKey((k) => (k === key ? null : k)), 1500);
  }, []);

  const openShare = useCallback((intent, item) => {
    const url = photoShareUrl(item);
    const text = "From the CommonSense Democrats reception recap:";
    const enc = encodeURIComponent;
    let href = null;
    if (intent === "x") href = `https://x.com/intent/tweet?text=${enc(text)}&url=${enc(url)}`;
    else if (intent === "facebook") href = `https://www.facebook.com/sharer/sharer.php?u=${enc(url)}`;
    else if (intent === "linkedin") href = `https://www.linkedin.com/sharing/share-offsite/?url=${enc(url)}`;
    else if (intent === "email") href = `mailto:?subject=${enc("Photos from the CommonSense Democrats reception")}&body=${enc(text + "\n\n" + url)}`;
    else if (intent === "sms") href = `sms:?&body=${enc(text + " " + url)}`;
    if (href) window.open(href, intent === "email" || intent === "sms" ? "_self" : "_blank", "noopener,noreferrer");
  }, []);

  // Deep-link support:
  //   /#recap                     -> scroll to the section
  //   /#photo-community-005       -> scroll + auto-open the lightbox on that photo
  useEffect(() => {
    if (!items.length) return;
    const applyHash = () => {
      const raw = (window.location.hash || "").replace(/^#/, "");
      if (!raw) return;
      if (raw === "recap") {
        rootRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
        return;
      }
      const m = raw.match(/^photo-((?:community|leadership)-\d+)$/i);
      if (!m) return;
      const base = m[1].toLowerCase();
      const idx = items.findIndex((it) => (it.src || "").toLowerCase().startsWith(base + "."));
      if (idx >= 0) {
        // If the item lives in a filter the user isn't currently viewing, switch to "all"
        // so the requested photo is always in-range.
        if (filter !== "all" && !items[idx].src.startsWith(filter + "-")) {
          setFilter("all");
        }
        rootRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
        // Defer to allow layout/scroll to settle before opening the lightbox
        setTimeout(() => setLightboxIndex(idx), 300);
      }
    };
    applyHash();
    window.addEventListener("hashchange", applyHash);
    return () => window.removeEventListener("hashchange", applyHash);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [items.length]);

  // Lightbox keyboard navigation.
  useEffect(() => {
    if (lightboxIndex === null) return;
    const onKey = (e) => {
      if (e.key === "Escape") setLightboxIndex(null);
      else if (e.key === "ArrowRight") setLightboxIndex((i) => (i + 1) % items.length);
      else if (e.key === "ArrowLeft") setLightboxIndex((i) => (i - 1 + items.length) % items.length);
    };
    window.addEventListener("keydown", onKey);
    // pause the carousel autoplay while the lightbox is open
    autoplay.current?.stop();
    return () => {
      window.removeEventListener("keydown", onKey);
      if (isPlaying) autoplay.current?.play();
    };
  }, [lightboxIndex, items.length, isPlaying]);

  const chip = (id, label, count) => (
    <button
      key={id}
      type="button"
      data-testid={`recap-filter-${id}`}
      onClick={() => setFilter(id)}
      className={`rounded-full px-4 py-2 text-xs md:text-sm font-bold uppercase tracking-widest border transition-all ${
        filter === id
          ? "bg-blue-500 border-blue-400 text-white shadow-lg shadow-blue-500/30"
          : "bg-slate-900/60 border-blue-500/25 text-gray-300 hover:border-blue-400 hover:text-white"
      }`}
    >
      {label} <span className="ml-1 opacity-70">({count})</span>
    </button>
  );

  if (items.length === 0) return null;
  const active = items[selectedIndex] || items[0];

  return (
    <section
      id="recap"
      ref={rootRef}
      className="rounded-2xl border border-blue-500/20 bg-slate-900/60 p-6 md:p-8"
      data-testid="recap-carousel"
    >
      <div className="text-center mb-6">
        <p className="text-blue-400 text-xs font-bold uppercase tracking-[0.25em] mb-2">Reception Recap</p>
        <h3 className="text-2xl md:text-3xl font-black text-white mb-2">
          Two Rooms. One Movement.
        </h3>
        <p className="text-gray-400 text-sm max-w-2xl mx-auto">
          Photos from an afternoon in Washington, D.C. that brought neighbors, organizers, and elected leaders into the same conversation.
        </p>
      </div>

      {/* Filter chips + Share this gallery */}
      <div className="flex flex-wrap items-center justify-center gap-2 md:gap-3 mb-6">
        {chip("all", "All", counts.all)}
        {chip("community", "Community Reception", counts.community)}
        {chip("leadership", "Leadership Reception", counts.leadership)}
        <button
          type="button"
          data-testid="recap-share-gallery"
          onClick={() => copyToClipboard(gallerySectionUrl(), "gallery")}
          title="Copy gallery link"
          className="ml-1 inline-flex items-center gap-2 rounded-full px-4 py-2 text-xs md:text-sm font-bold uppercase tracking-widest border border-pink-500/40 bg-pink-500/10 text-pink-300 hover:bg-pink-500/20 hover:text-pink-200 transition-all"
        >
          {copiedKey === "gallery" ? <Check className="w-4 h-4" /> : <Share2 className="w-4 h-4" />}
          <span>{copiedKey === "gallery" ? "Link Copied" : "Share Gallery"}</span>
        </button>
      </div>

      {/* Hero carousel */}
      <div className="relative rounded-xl overflow-hidden bg-black">
        <div className="embla" ref={emblaRef}>
          <div className="embla__container flex">
            {items.map((it, idx) => (
              <div
                key={`${it.event}-${it.src}-${idx}`}
                className="embla__slide relative shrink-0 grow-0 basis-full aspect-[16/9] bg-black flex items-center justify-center cursor-zoom-in"
                onClick={() => setLightboxIndex(idx)}
                data-testid={`recap-slide-${idx}`}
              >
                {it.type === "video" ? (
                  <>
                    <img
                      src={it.posterUrl || it.thumb}
                      alt=""
                      className="w-full h-full object-contain"
                      loading={idx < 3 ? "eager" : "lazy"}
                    />
                    <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                      <span className="rounded-full bg-white/90 text-slate-900 p-4 shadow-xl">
                        <Play className="w-8 h-8" fill="currentColor" />
                      </span>
                    </div>
                    <span className="absolute top-3 left-3 rounded-full bg-black/70 text-white text-[10px] font-bold uppercase tracking-widest px-2 py-1">
                      Video
                    </span>
                  </>
                ) : (
                  <img
                    src={it.url}
                    alt=""
                    className="w-full h-full object-contain"
                    loading={idx < 3 ? "eager" : "lazy"}
                  />
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Chevron controls */}
        <button
          type="button"
          data-testid="recap-prev"
          onClick={scrollPrev}
          aria-label="Previous"
          className="absolute left-2 top-1/2 -translate-y-1/2 rounded-full bg-black/60 hover:bg-black/80 text-white p-2 md:p-3 transition-all"
        >
          <ChevronLeft className="w-5 h-5 md:w-6 md:h-6" />
        </button>
        <button
          type="button"
          data-testid="recap-next"
          onClick={scrollNext}
          aria-label="Next"
          className="absolute right-2 top-1/2 -translate-y-1/2 rounded-full bg-black/60 hover:bg-black/80 text-white p-2 md:p-3 transition-all"
        >
          <ChevronRight className="w-5 h-5 md:w-6 md:h-6" />
        </button>

        {/* Bottom overlay: counter + play/pause + event chip */}
        <div className="absolute inset-x-0 bottom-0 flex items-center justify-between gap-3 px-3 py-2 bg-gradient-to-t from-black/80 to-transparent">
          <span className="rounded-full bg-black/70 text-white text-[10px] md:text-xs font-bold uppercase tracking-widest px-3 py-1">
            {active?.event === "community" ? "Community" : "Leadership"}
          </span>
          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={toggleAutoplay}
              aria-label={isPlaying ? "Pause" : "Play"}
              className="rounded-full bg-black/60 hover:bg-black/80 text-white p-1.5 transition-all"
              data-testid="recap-autoplay-toggle"
            >
              {isPlaying ? <Pause className="w-3.5 h-3.5" /> : <Play className="w-3.5 h-3.5" />}
            </button>
            <span className="text-white text-[11px] md:text-xs font-mono tabular-nums" data-testid="recap-counter">
              {selectedIndex + 1} / {items.length}
            </span>
          </div>
        </div>
      </div>

      {/* Dot indicators (only render when count is manageable) */}
      {scrollSnaps.length > 0 && scrollSnaps.length <= 30 && (
        <div className="flex flex-wrap items-center justify-center gap-1.5 mt-4">
          {scrollSnaps.map((_, i) => (
            <button
              key={i}
              type="button"
              onClick={() => emblaApi && emblaApi.scrollTo(i)}
              aria-label={`Go to slide ${i + 1}`}
              className={`h-1.5 rounded-full transition-all ${
                i === selectedIndex ? "w-6 bg-blue-400" : "w-1.5 bg-slate-600 hover:bg-slate-400"
              }`}
            />
          ))}
        </div>
      )}

      {/* Lightbox */}
      {lightboxIndex !== null && items[lightboxIndex] && (
        <div
          role="dialog"
          aria-modal="true"
          className="fixed inset-0 z-[100] bg-black/95 flex items-center justify-center p-4"
          onClick={() => setLightboxIndex(null)}
          data-testid="recap-lightbox"
        >
          <button
            type="button"
            aria-label="Close"
            onClick={(e) => { e.stopPropagation(); setLightboxIndex(null); }}
            className="absolute top-4 right-4 rounded-full bg-white/10 hover:bg-white/20 text-white p-2"
            data-testid="recap-lightbox-close"
          >
            <X className="w-5 h-5" />
          </button>

          {/* Share toolbar */}
          <div
            className="absolute top-4 left-4 flex items-center gap-1.5 rounded-full bg-white/10 backdrop-blur px-2 py-1.5"
            onClick={(e) => e.stopPropagation()}
            data-testid="recap-lightbox-share"
          >
            {[
              { id: "x", label: "Share on X", Icon: Twitter },
              { id: "facebook", label: "Share on Facebook", Icon: Facebook },
              { id: "linkedin", label: "Share on LinkedIn", Icon: Linkedin },
              { id: "email", label: "Share via email", Icon: Mail },
              { id: "sms", label: "Share via SMS", Icon: MessageSquare },
            ].map(({ id, label, Icon }) => (
              <button
                key={id}
                type="button"
                aria-label={label}
                title={label}
                data-testid={`recap-lightbox-share-${id}`}
                onClick={() => openShare(id, items[lightboxIndex])}
                className="rounded-full text-white hover:bg-white/15 p-2 transition-all"
              >
                <Icon className="w-4 h-4" />
              </button>
            ))}
            <button
              type="button"
              aria-label={copiedKey === "photo" ? "Link copied" : "Copy link to photo"}
              title={copiedKey === "photo" ? "Link copied" : "Copy link to photo"}
              data-testid="recap-lightbox-share-copy"
              onClick={() => copyToClipboard(photoShareUrl(items[lightboxIndex]), "photo")}
              className="rounded-full text-white hover:bg-white/15 p-2 transition-all"
            >
              {copiedKey === "photo" ? <Check className="w-4 h-4 text-green-300" /> : <Link2 className="w-4 h-4" />}
            </button>
          </div>

          <button
            type="button"
            aria-label="Previous"
            onClick={(e) => { e.stopPropagation(); setLightboxIndex((i) => (i - 1 + items.length) % items.length); }}
            className="absolute left-4 top-1/2 -translate-y-1/2 rounded-full bg-white/10 hover:bg-white/20 text-white p-3"
          >
            <ChevronLeft className="w-6 h-6" />
          </button>
          <button
            type="button"
            aria-label="Next"
            onClick={(e) => { e.stopPropagation(); setLightboxIndex((i) => (i + 1) % items.length); }}
            className="absolute right-4 top-1/2 -translate-y-1/2 rounded-full bg-white/10 hover:bg-white/20 text-white p-3"
          >
            <ChevronRight className="w-6 h-6" />
          </button>

          <div
            className="relative w-full max-w-6xl max-h-[85vh] flex items-center justify-center"
            onClick={(e) => e.stopPropagation()}
          >
            {items[lightboxIndex].type === "video" ? (
              <video
                key={items[lightboxIndex].url}
                src={items[lightboxIndex].url}
                poster={items[lightboxIndex].posterUrl || undefined}
                controls
                autoPlay
                playsInline
                className="max-w-full max-h-[85vh] rounded-md"
              />
            ) : (
              <img
                src={items[lightboxIndex].url}
                alt=""
                className="max-w-full max-h-[85vh] rounded-md"
              />
            )}
          </div>

          <div className="absolute bottom-4 inset-x-0 text-center text-white text-xs md:text-sm font-mono tabular-nums pointer-events-none">
            {lightboxIndex + 1} / {items.length} &middot;{" "}
            {items[lightboxIndex].event === "community" ? "Community Reception" : "Leadership Reception"}
          </div>
        </div>
      )}
    </section>
  );
}
